/**
 * The bank, from the paying parent's side.
 *
 * Two acts live here and nothing else: putting money in, and handing it over.
 * Everything a child does with their money — sorting it, watching it grow —
 * belongs on their own screen, one link away.
 *
 * The screen leads with what the family owes rather than with the children,
 * because that is the number the plan was missing entirely (PLAN_REVIEW.md §7)
 * and the one that keeps the ledger honest. Without it, balances only ever
 * grow: money coming in is recorded, money going out is remembered by nobody,
 * and a parent asking "what do I actually owe?" has to add it up on paper.
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRightIcon } from '../ui/Icons.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useFamily } from '../lib/family.jsx';
import {
  BUCKET_LABEL, CADENCE_RATE, EMPTY_BALANCE, METHOD_LABEL, deposit, settle, useBank,
} from '../lib/bank.js';
import { BUCKETS, SETTLEMENT_METHODS } from '../domain/ledger.js';
import { allowanceAt } from '../domain/allowance.js';
import { centsFromDollars, formatCents } from '../domain/money.js';

const dollars = (cents) => (cents / 100).toFixed(2);

/** Small change reads as small change. "$0.12 still owed" reads as a bug. */
const owedLabel = (cents) => (cents > 0 && cents < 100 ? `${cents}¢` : formatCents(cents));

function DepositForm({ onSubmit, busy }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const cents = centsFromDollars(amount);

  return (
    <form
      className="stack tight"
      onSubmit={(e) => { e.preventDefault(); onSubmit({ cents, note: note.trim() || null }); }}
    >
      <div className="row">
        <label style={{ flex: '1 1 7rem' }}>
          Amount in dollars
          <input
            type="number" inputMode="decimal" min="0" step="0.01" required autoFocus
            value={amount} onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label style={{ flex: '2 1 12rem' }}>
          What it was (optional)
          <input
            value={note} maxLength={80} placeholder="Birthday from Grandma"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>
      {/*
        It lands unsorted, and the screen says so before the button is pressed.
        Deciding where money goes is the child's job and the whole lesson — see
        `project` in domain/ledger.js — so the app never quietly files it.
      */}
      <p className="small muted">
        It arrives unsorted, for them to split between Spend, Save and Give.
      </p>
      <button className="primary" type="submit" disabled={busy || cents <= 0}>
        {busy ? 'Adding…' : 'Add it'}
      </button>
    </form>
  );
}

function PayoutForm({ balance, onSubmit, busy }) {
  // "Unsorted" is offered only when there is unsorted money, because a parent
  // can be asked for money the child hasn't split yet and the alternative is a
  // balance that can be owed but never paid.
  const options = [
    ...BUCKETS.map((b) => ({ value: b, label: BUCKET_LABEL[b], cents: balance[b] })),
    ...(balance.unallocated > 0
      ? [{ value: 'unallocated', label: 'Unsorted', cents: balance.unallocated }]
      : []),
  ];
  const [bucket, setBucket] = useState(options[0].value);
  const current = options.find((o) => o.value === bucket) ?? options[0];
  const [owed, setOwed] = useState(dollars(Math.max(current.cents, 0)));
  const [paid, setPaid] = useState(dollars(Math.max(current.cents, 0)));
  const [method, setMethod] = useState(SETTLEMENT_METHODS[0]);
  const owedCents = centsFromDollars(owed);
  const paidCents = centsFromDollars(paid);

  function pickBucket(value) {
    const next = options.find((o) => o.value === value);
    setBucket(value);
    // Both fields follow the bucket, because the common payout is the whole of
    // it and typing the same number twice is the tax on the uncommon one.
    setOwed(dollars(Math.max(next?.cents ?? 0, 0)));
    setPaid(dollars(Math.max(next?.cents ?? 0, 0)));
  }

  return (
    <form
      className="stack tight"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          bucket: bucket === 'unallocated' ? null : bucket,
          owedCents,
          paidCents,
          method,
        });
      }}
    >
      <div className="row">
        <label style={{ flex: '1 1 8rem' }}>
          Out of
          <select value={bucket} onChange={(e) => pickBucket(e.target.value)}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} — {formatCents(o.cents)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ flex: '1 1 7rem' }}>
          Owed
          <input
            type="number" inputMode="decimal" min="0" step="0.01" required
            value={owed} onChange={(e) => setOwed(e.target.value)}
          />
        </label>
        <label style={{ flex: '1 1 7rem' }}>
          Actually paid
          <input
            type="number" inputMode="decimal" min="0" step="0.01" required
            value={paid} onChange={(e) => setPaid(e.target.value)}
          />
        </label>
      </div>
      <label>
        How
        <select value={method} onChange={(e) => setMethod(e.target.value)}>
          {SETTLEMENT_METHODS.map((m) => (
            <option key={m} value={m}>{METHOD_LABEL[m]}</option>
          ))}
        </select>
      </label>
      {/*
        Settling $4.37 in coins is the ordinary case, not an edge one. The two
        fields are separate so the 12¢ has somewhere to go other than quietly
        off the ledger.
      */}
      <p className="small muted">
        Paid less than owed? The difference stays on their balance.
      </p>
      <button className="primary" type="submit" disabled={busy || owedCents <= 0 || paidCents <= 0}>
        {busy ? 'Saving…' : 'Record it'}
      </button>
    </form>
  );
}

export default function Bank() {
  const { family, kids, profile } = useFamily();
  const { user } = useAuth();
  const bank = useBank(family?.id);
  // Which child's card has a form open, and which form. One at a time: two
  // amount fields on screen at once is how money gets typed into the wrong one.
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [remainders, setRemainders] = useState({});

  // A child looking at the bank sees their own bank. Nobody else's numbers are
  // on this screen for them to read.
  if (profile) return <Navigate to={`/bank/${profile.id}`} replace />;

  const openForm = (childId, mode) => {
    // The "still owed" line belongs to the payout that produced it. Leaving it
    // up while a second one is being typed would attach it to the wrong money.
    setRemainders((r) => ({ ...r, [childId]: 0 }));
    setOpen((o) => (o?.childId === childId && o.mode === mode ? null : { childId, mode }));
  };

  async function addMoney(childId, { cents, note }) {
    setBusy(true); setError(null);
    try {
      await deposit({
        familyId: family.id, childId, cents, note, actorId: user.id, occurredAt: bank.today,
      });
      setOpen(null);
      await bank.reload();
    } catch (e) {
      setError(`That deposit didn’t save — ${e.message}.`);
    }
    setBusy(false);
  }

  async function payOut(childId, values) {
    setBusy(true); setError(null);
    try {
      const { remainderCents } = await settle({
        familyId: family.id, childId, ...values, actorId: user.id, occurredAt: bank.today,
      });
      setRemainders((r) => ({ ...r, [childId]: remainderCents }));
      setOpen(null);
      await bank.reload();
    } catch (e) {
      setError(`That payout didn’t save — ${e.message}.`);
    }
    setBusy(false);
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <p className="eyebrow">{family.name}</p>
        <h1>The bank</h1>
      </div>

      {/*
        The single most important number in the product for the paying parent,
        and the one PLAN_REVIEW.md §7 found missing: outstanding obligation, the
        sum of every child's balance. Each of those balances is a promise rather
        than a deposit, so this is money the household still owes.
      */}
      <div className="card stack tight">
        <p className="eyebrow">What you owe</p>
        <p className="money" style={{ fontSize: 'var(--size-2xl)' }}>
          {formatCents(bank.outstandingCents)}
        </p>
        <p className="small muted">
          Across everyone. It is money you are holding for them, not money in an account.
        </p>
      </div>

      {bank.error && <p className="notice error">{bank.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}

      {bank.loading && <p className="muted" role="status">Loading…</p>}

      {!bank.loading && kids.length === 0 && (
        <div className="card stack">
          <h2>No crew yet</h2>
          <p className="muted">Add the kids and there is somewhere for the money to go.</p>
          <Link to="/children"><button className="primary">Add a child</button></Link>
        </div>
      )}

      {!bank.loading && kids.map((kid) => {
        const balance = bank.byChild[kid.id]?.balance ?? EMPTY_BALANCE;
        const remainder = remainders[kid.id] ?? 0;
        // What the job will actually pay this child, priced from their birth
        // month and year at today's date — never a stored number, and never the
        // target, which includes the part that still has to be earned.
        const allowance = bank.allowanceRules[kid.id] ?? null;
        const allowanceCents = allowance
          ? allowanceAt(
            bank.today,
            { birthMonth: kid.birth_month, birthYear: kid.birth_year },
            allowance,
          ).baseCents
          : 0;
        return (
          <div className="card stack tight" key={kid.id}>
            {/* The heading is the way in. A bordered "Open" button beside
                "Add money" and "Pay out" read as a third action; a chevron on
                the name reads as a door. */}
            <Link to={`/bank/${kid.id}`} className="cardhead">
              <h2>{kid.display_name}</h2>
              <span className="money">{formatCents(balance.total)}</span>
              <ChevronRightIcon size={20} />
            </Link>

            <ul className="stack tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {BUCKETS.map((b) => (
                <li className="spread small" key={b}>
                  <span className="muted">{BUCKET_LABEL[b]}</span>
                  <span className="money">{formatCents(balance[b])}</span>
                </li>
              ))}
              {balance.unallocated > 0 && (
                <li className="spread small">
                  <span className="muted">Unsorted</span>
                  <span className="money">{formatCents(balance.unallocated)}</span>
                </li>
              )}
            </ul>

            {/*
              One line, because a recurring commitment that is invisible on the
              screen listing what you owe is a commitment nobody re-reads. It is
              a link rather than a figure so the fix for a wrong number is where
              the number is.
            */}
            <p className="small">
              <Link to={`/bank/${kid.id}/allowance`}>
                {allowance
                  ? `${formatCents(allowanceCents)} ${CADENCE_RATE[allowance.cadence] ?? CADENCE_RATE.weekly}`
                  : 'No allowance set'}
              </Link>
            </p>

            <div className="row">
              <button onClick={() => openForm(kid.id, 'deposit')}>
                {open?.childId === kid.id && open.mode === 'deposit' ? 'Cancel' : 'Add money'}
              </button>
              <button onClick={() => openForm(kid.id, 'settle')} disabled={balance.total <= 0}>
                {open?.childId === kid.id && open.mode === 'settle' ? 'Cancel' : 'Pay out'}
              </button>
            </div>

            {open?.childId === kid.id && open.mode === 'deposit' && (
              <DepositForm busy={busy} onSubmit={(v) => addMoney(kid.id, v)} />
            )}
            {open?.childId === kid.id && open.mode === 'settle' && (
              <PayoutForm balance={balance} busy={busy} onSubmit={(v) => payOut(kid.id, v)} />
            )}

            {remainder > 0 && (
              <p className="small muted">
                {owedLabel(remainder)} still owed — it stays on the ledger.
              </p>
            )}
          </div>
        );
      })}
    </main>
  );
}
